/**
 * Economic calendar: Tehran week maths and Persian labels. Safe for server and client.
 * Tehran has been UTC+3:30 all year since 1401 (no daylight saving).
 */
import { TEHRAN_OFFSET_SEC, tehranDayStart } from '@/utils/gold/mazanehFormat';

export type Impact = 'low' | 'medium' | 'high';

/** Seconds of the Tehran Saturday 00:00 that starts the week containing `ms`. */
export const tehranWeekStart = (ms: number): number => {
    const dayStart = tehranDayStart(ms);
    // getUTCDay of the shifted time is the Tehran weekday (Saturday = 6)
    const weekday = new Date((dayStart + TEHRAN_OFFSET_SEC) * 1000).getUTCDay();
    return dayStart - ((weekday + 1) % 7) * 86400;
};

/** Countries shown when the reader asks for medium events too (the big currencies). */
export const MAIN_COUNTRIES = ['US', 'EU', 'GB', 'JP', 'CN', 'CA', 'AU', 'CH', 'NZ', 'DE'];

const COUNTRY_FA: Record<string, string> = {
    US: 'آمریکا',
    EU: 'منطقهٔ یورو',
    GB: 'انگلیس',
    JP: 'ژاپن',
    CN: 'چین',
    CA: 'کانادا',
    AU: 'استرالیا',
    NZ: 'نیوزیلند',
    CH: 'سوئیس',
    DE: 'آلمان',
    FR: 'فرانسه',
    IT: 'ایتالیا',
    ES: 'اسپانیا',
    IN: 'هند',
    KR: 'کرهٔ جنوبی',
    BR: 'برزیل',
    MX: 'مکزیک',
    ZA: 'آفریقای جنوبی',
    SG: 'سنگاپور',
    HK: 'هنگ‌کنگ',
    TR: 'ترکیه',
    SA: 'عربستان',
    AE: 'امارات',
    RU: 'روسیه',
};

export const countryFa = (code: string): string => COUNTRY_FA[code] || code;

export const IMPACT_FA: Record<Impact, string> = {
    high: 'زیاد',
    medium: 'متوسط',
    low: 'کم',
};

export const DIRECTIONALITY_FA: Record<string, string> = {
    'higher-may-support-currency': 'عدد بالاتر از انتظار معمولاً به نفع ارز آن کشور است',
    'lower-may-support-currency': 'عدد پایین‌تر از انتظار معمولاً به نفع ارز آن کشور است',
    'context-dependent': 'اثرش به شرایط بستگی دارد',
    'non-directional': 'جهت مشخصی برای ارز ندارد',
};

/** «54.5» → «۵۴٫۵», «-1.892M» → «−۱٫۸۹۲M». Empty → «—». */
export const faValue = (raw?: string | null): string => {
    if (!raw || !raw.trim()) return '—';
    return raw
        .trim()
        .replace(/^-/, '−')
        .replace(/(\d)\.(\d)/g, '$1٫$2')
        .replace(/[0-9]/g, (d) => '۰۱۲۳۴۵۶۷۸۹'[Number(d)]);
};

const dayFmt = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    timeZone: 'Asia/Tehran',
    weekday: 'long',
    day: 'numeric',
    month: 'long',
});
const dayMonthFmt = new Intl.DateTimeFormat('fa-IR-u-ca-persian', {
    timeZone: 'Asia/Tehran',
    day: 'numeric',
    month: 'long',
});
const timeFmt = new Intl.DateTimeFormat('fa-IR', {
    timeZone: 'Asia/Tehran',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
});

const parts = (fmt: Intl.DateTimeFormat, sec: number) =>
    Object.fromEntries(fmt.formatToParts(new Date(sec * 1000)).map((p) => [p.type, p.value]));

/** «شنبه ۹ آبان» */
export const tehranDayLabel = (sec: number): string => {
    const p = parts(dayFmt, sec);
    return `${p.weekday} ${p.day} ${p.month}`;
};

/** «۹ آبان» */
export const tehranDayMonth = (sec: number): string => {
    const p = parts(dayMonthFmt, sec);
    return `${p.day} ${p.month}`;
};

/** «۱۷:۰۰» */
export const tehranClock = (sec: number): string => timeFmt.format(new Date(sec * 1000));
